import React, { useEffect, useState, useRef } from 'react';
import { ComponentLoader } from '../common/Loader';
import { useDispatch } from 'react-redux';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import { fetchFeatureUsageByGender, fetchUsersLocation } from "./store";

const FeatureUsage = ({ locations = [] }) => {
    const dispatch = useDispatch();
    const [featureUsageData, setFeatureUsageData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [locationsLocal, setLocationsLocal] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('');
    const dataFetchedRef = useRef(false);

    // Fetch User Locations and Feature Usage by Gender on mount
    useEffect(() => {
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;

        setLoading(true);
        const cacheKey = `FEATURE_USAGE_BY_GENDER_CACHE`;
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
            const data = JSON.parse(cachedData);
            if (data === 'PENDING') return;

            const cacheTimestamp = data.cacheTimestamp || 0;
            const threeMinutes = 3 * 60 * 1000;
            const isExpired = Date.now() - cacheTimestamp > threeMinutes;
            if (!isExpired) {
                setFeatureUsageData(data.featureUsageData);
                setLocationsLocal(data.locationsLocal);
                setSelectedLocation(data.selectedLocation);
                setLoading(false);
                return;
            } else {
                sessionStorage.removeItem(cacheKey);
            }
        }

        // Mark as pending
        sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));
        // Fetch Feature Usage by Gender for Bar Chart (initial load - all locations)
        dispatch(fetchFeatureUsageByGender(''))
            .then((response) => {
                if (response.payload && response.payload.success) {
                    const { bloodPressure, moodTracker, Steps, bloodSugar } = response.payload.data;
                    const chartData = [
                        { name: 'Blood Pressure', Male: bloodPressure.Male, Female: bloodPressure.Female, Other: bloodPressure.Other },
                        { name: 'Mood Tracker', Male: moodTracker.Male, Female: moodTracker.Female, Other: moodTracker.Other },
                        { name: 'Steps', Male: Steps.Male, Female: Steps.Female, Other: Steps.Other },
                        { name: 'Blood Sugar', Male: bloodSugar.Male, Female: bloodSugar.Female, Other: bloodSugar.Other },
                    ];
                    setFeatureUsageData(chartData);
                    sessionStorage.setItem('FEATURE_USAGE_BY_GENDER_CACHE', JSON.stringify({
                        featureUsageData: chartData,
                        locationsLocal: [],
                        selectedLocation: '',
                        cacheTimestamp: Date.now()
                    }));
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching feature usage by gender:', error);
                setLoading(false);
            });
    }, [dispatch]);

    // Handle location change and re-fetch data
    // const handleLocationChange = (e) => {
    //     const location = e.target.value;
    //     setSelectedLocation(location);
    //     setLoading(true);

    //     dispatch(fetchFeatureUsageByGender(location))
    //         .then((response) => {
    //             if (response.payload && response.payload.success) {
    //                 const { bloodPressure, moodTracker, Steps, bloodSugar } = response.payload.data;
    //                 const chartData = [
    //                     { name: 'Blood Pressure', Male: bloodPressure.Male, Female: bloodPressure.Female, Other: bloodPressure.Other },
    //                     { name: 'Mood Tracker', Male: moodTracker.Male, Female: moodTracker.Female, Other: moodTracker.Other },
    //                     { name: 'Steps', Male: Steps.Male, Female: Steps.Female, Other: Steps.Other },
    //                     { name: 'Blood Sugar', Male: bloodSugar.Male, Female: bloodSugar.Female, Other: bloodSugar.Other },
    //                 ];
    //                 setFeatureUsageData(chartData);
    //             }
    //             setLoading(false);
    //         })
    //         .catch((error) => {
    //             console.error('Error fetching feature usage by gender:', error);
    //             setLoading(false);
    //         });
    // };

    return (
        <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            flex: '1',
            minWidth: '380px',
            height: '450px'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '4px', height: '20px', background: '#3b82f6', borderRadius: '2px' }}></div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#333' }}>Feature Usage by Gender</h3>
                </div>
                {/* <select
                    value={selectedLocation}
                    onChange={handleLocationChange}
                    style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        fontSize: '13px',
                        color: '#374151',
                        background: '#f9fafb',
                        cursor: 'pointer',
                        minWidth: '150px'
                    }}
                >
                    <option value="">All Locations</option>
                    {locations.map((loc, index) => (
                        <option key={index} value={loc.city}>{loc.city}</option>
                    ))}
                </select> */}
            </div>
            {loading ? (
                <div style={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ComponentLoader color="#3b82f6" size="40px" />
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={featureUsageData} barGap={2} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <Tooltip
                            content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                    return (
                                        <div style={{
                                            backgroundColor: '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            padding: '12px 16px'
                                        }}>
                                            <p style={{ fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>{label}</p>
                                            {payload.map((entry, index) => {
                                                if (label === 'Period Tracker' && entry.name === 'Male') return null;
                                                return (
                                                    <p key={index} style={{ padding: '2px 0', color: entry.fill, fontSize: '12px' }}>
                                                        {entry.name} : {entry.value}
                                                    </p>
                                                );
                                            })}
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                            iconSize={10}
                            formatter={(value) => <span style={{ color: '#666', fontSize: '12px' }}>{value}</span>}
                        />
                        <Bar dataKey="Female" fill="#EC4899" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Male" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Other" fill="#9CA3AF" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default FeatureUsage;
